import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, UserCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { services } from "@/data/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) {
    return (
      <section className="bg-[#0B1F3A]">
        <div className="mx-auto max-w-3xl px-5 py-28 text-center" data-testid="service-not-found">
          <h1 className="font-display text-2xl font-bold text-white">This service page no longer exists</h1>
          <p className="mt-3 text-sm text-white/60">Our services have been updated. Explore the current range below.</p>
          <Link to="/services" data-testid="service-not-found-link" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#C9A227] px-6 py-3 text-sm font-bold text-[#0B1F3A] hover:bg-[#DDB845]">
            View our services
          </Link>
        </div>
      </section>
    );
  }
  const idx = services.indexOf(service);
  const next = services[(idx + 1) % services.length];

  return (
    <>
      <Seo title={`${service.title} | Incremental Consulting`} description={service.short} />
      <section className="bg-[#0B1F3A]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <Reveal>
            <Link to="/services" data-testid="service-detail-back" className="inline-flex items-center gap-2 text-[13px] font-semibold text-white/60 transition-colors hover:text-white">
              <ArrowLeft size={15} /> All Services
            </Link>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.22em] text-[#C9A227]">{service.category}</p>
            <h1 className="mt-3 max-w-3xl text-balance font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="service-detail-title">
              {service.title}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70" data-testid="service-detail-overview">
              {service.description}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F5F8FC]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <div className="grid gap-6 lg:grid-cols-5">
            <Reveal className="lg:col-span-3">
              <div className="h-full rounded-xl border border-[#0B1F3A]/10 bg-white p-8" data-testid="service-included">
                <h2 className="font-display text-lg font-bold text-[#0B1F3A]">What's included</h2>
                <ul className="mt-5 grid gap-3.5 sm:grid-cols-2">
                  {service.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm leading-relaxed text-[#172033]/85">
                      <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#A87B1F]" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="flex h-full flex-col rounded-xl bg-[#0B1F3A] p-8" data-testid="service-for">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#C9A227]/15 text-[#C9A227]">
                  <UserCheck size={20} />
                </span>
                <h2 className="mt-5 font-display text-lg font-bold text-white">Who it's for</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-white/70">{service.forWhom}</p>
                <Link
                  to={`/contact?service=${encodeURIComponent(service.title)}`}
                  data-testid="service-detail-cta"
                  className="group mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-[#C9A227] px-6 py-3 text-sm font-bold text-[#0B1F3A] transition-colors duration-200 hover:bg-[#DDB845]"
                >
                  Book a delivery review
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="mt-10">
            <Link to={`/services/${next.slug}`} data-testid="service-detail-next" className="text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]">
              Next: {next.title} →
            </Link>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
